/**
 * Translates technical errors into user-friendly Portuguese messages.
 *
 * Auth errors (JWT expired / unauthorized) trigger an automatic page reload
 * so the user gets a fresh session without manual intervention.
 */

import { toast } from "sonner";
import { announceError } from "@/lib/a11yLiveRegion";
import {
  isNoConfiguredProviderError,
  showNoConfiguredProviderToast,
} from "@/lib/edgeFnError";

// ── Pattern → friendly message map ──────────────────────────────────────────

const AUTH_PATTERNS = [
  /jwt expired/i,
  /jwt signature/i,
  /invalid token/i,
  /token expired/i,
  /missing sub claim/i,
  /unauthorized/i,
  /not authenticated/i,
  /session expired/i,
];

const NETWORK_PATTERNS = [
  /network error/i,
  /failed to fetch/i,
  /load failed/i,
  /net::err/i,
];

const PERMISSION_PATTERNS = [
  /permission denied/i,
  /row-level security/i,
  /violates row/i,
  /42501/i,
  /403/,
  /insufficient_scope/i,
];

const NOT_FOUND_PATTERNS = [/not found/i, /does not exist/i, /404/];

const OVERLOAD_PATTERNS = [
  /rate limit/i,
  /too many requests/i,
  /429/,
  /quota/i,
];

const ACTIVE_SIMULATION_PATTERNS = [
  /simulator_sessions_user_active_uniq/i,
];

function matchesAny(msg: string, patterns: RegExp[]) {
  return patterns.some((p) => p.test(msg));
}

// ── Public API ───────────────────────────────────────────────────────────────

export function isAuthError(err: unknown): boolean {
  const msg = extractMessage(err);
  return matchesAny(msg, AUTH_PATTERNS);
}

/**
 * Returns a user-friendly message for any error.
 * Falls back to `fallback` when no pattern matches and the raw message is
 * too technical (contains English words, codes, etc.).
 */
export function friendlyError(err: unknown, fallback?: string): string {
  // BYOK: caller has no usable key for any provider in the chain. We surface
  // a dedicated CTA toast in `toastError`, so the inline string here is just
  // a courteous fallback for callers that render it elsewhere (e.g. inline
  // error labels under form fields).
  if (isNoConfiguredProviderError(err)) {
    return "Conecte uma chave da OpenAI para usar este recurso.";
  }

  const raw = extractMessage(err);

  if (matchesAny(raw, ACTIVE_SIMULATION_PATTERNS)) {
    return "Você já tem uma simulação em andamento. Continue ou encerre a atual antes de iniciar outra.";
  }

  if (matchesAny(raw, AUTH_PATTERNS)) {
    return "Sua sessão expirou. A página será atualizada automaticamente.";
  }
  if (matchesAny(raw, NETWORK_PATTERNS)) {
    return "Sem conexão com a internet. Verifique sua rede e tente novamente.";
  }
  if (matchesAny(raw, PERMISSION_PATTERNS)) {
    return "Você não tem permissão para realizar esta ação.";
  }
  if (matchesAny(raw, NOT_FOUND_PATTERNS)) {
    return "O item solicitado não foi encontrado.";
  }
  if (matchesAny(raw, OVERLOAD_PATTERNS)) {
    return "Muitas tentativas em pouco tempo. Aguarde um momento e tente novamente.";
  }

  // If the raw message looks like it contains technical jargon, use fallback.
  if (fallback && looksLikeTechnical(raw)) return fallback;

  // Otherwise the raw message is probably already in Portuguese and friendly.
  return raw || fallback || "Ocorreu um erro inesperado. Tente novamente.";
}

/**
 * Shows a toast with a friendly error message.
 * Auth errors also trigger an automatic page reload after 2 s.
 */
export function toastError(err: unknown, fallback?: string) {
  // Special-case: route the BYOK "no configured provider" signal to the
  // dedicated CTA toast. Skip the generic toast entirely (the CTA toast
  // already announces itself to the live region).
  if (isNoConfiguredProviderError(err)) {
    showNoConfiguredProviderToast();
    return;
  }

  const msg = friendlyError(err, fallback);
  toast.error(msg);
  // A4-A11y — assertive announcement (Sonner default is polite).
  announceError(msg);

  if (isAuthError(err)) {
    scheduleReload();
  }
}

/**
 * A4-A11y — assertive variant of `toast.error` for callers that import
 * sonner directly. Behaves like `toast.error` but also pushes the message
 * to the assertive live region so screen readers interrupt-announce it.
 */
export function notifyError(message: string) {
  toast.error(message);
  announceError(message);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function extractMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message ?? "";
  if (typeof err === "object" && "message" in err) return String((err as { message: unknown }).message);
  return "";
}

/** Heuristic: contains English words, HTTP codes, stack frames, or Supabase codes. */
function looksLikeTechnical(msg: string): boolean {
  return /[A-Z]{3,}|PGRST|error:|Error:|failed|fetch|stack|at\s+\w+\s*\(/i.test(msg);
}

let reloadScheduled = false;

function scheduleReload() {
  if (reloadScheduled) return;
  reloadScheduled = true;
  setTimeout(() => {
    window.location.reload();
  }, 2000);
}
